import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaBoxes, FaEdit, FaPlus, FaSearch, FaTrashAlt } from "react-icons/fa";
import { obtenerItemsCatalogo, guardarItem } from "../../../services/contabilidad/logisticaService";
import { formatCurrency } from "../../GestionContable/utils";
import { logBusinessAction } from "../../../services/AuditService";

const BTN_GRADIENT = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const CatalogoItems = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingItem, setEditingItem] = useState({ codigo: "", descripcion: "", unidad: "UND", precio_ref: 0, categoria: "" });

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      const data = await obtenerItemsCatalogo(rucEmpresa);
      setItems(data);
    } catch (e) {
      setError("No se pudieron cargar los ítems del catálogo.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSave = async () => {
    if (!editingItem.codigo || !editingItem.descripcion) {
      setError("Código y Descripción son obligatorios.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const esEdicion = !!editingItem.id;
      await guardarItem(rucEmpresa, editingItem);

      // AUDITORÍA
      try {
        await logBusinessAction(
          esEdicion ? "EDICION_ITEM_COMPRA" : "CREACION_ITEM_COMPRA",
          "GestionCompras",
          {
            codigo: editingItem.codigo,
            descripcion: editingItem.descripcion,
            precio: editingItem.precio_ref
          },
          usuario
        );
      } catch (e) {
        console.warn("No se pudo registrar la auditoría del ítem de compra", e);
      }

      setSuccess("Ítem guardado correctamente.");
      setShowModal(false);
      await cargar();
    } catch (e) {
      setError("Error al guardar el ítem.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter(i => 
    i.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || 
    i.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="animate__animated animate__fadeIn">
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Barra de Búsqueda y Acciones */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  className="ps-5 border-0 bg-light rounded-pill py-2"
                  placeholder="Buscar producto o servicio por código o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button 
                style={BTN_GRADIENT} 
                className="rounded-pill px-4 shadow-sm"
                onClick={() => { setEditingItem({ codigo: "", descripcion: "", unidad: "UND", precio_ref: 0, categoria: "" }); setShowModal(true); }}
              >
                <FaPlus className="me-2" /> Agregar Item
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla de Items */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Código</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Descripción / Categoría</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Unidad</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Precio Ref.</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-5 text-muted">No hay ítems registrados en el catálogo.</td></tr>
                ) : filtered.map((i) => (
                  <tr key={i.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td className="px-4 py-3 fw-bold text-dark">{i.codigo}</td>
                    <td className="px-4 py-3">
                      <div className="fw-semibold text-dark">{i.descripcion}</div>
                      <Badge bg="light" text="dark" className="border small fw-normal">{i.categoria || "SIn categoría"}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge bg="secondary" className="bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-3">
                        {i.unidad}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-end fw-bold text-primary">{formatCurrency(i.precio_ref)}</td>
                    <td className="px-4 py-3 text-center">
                      <Button variant="link" className="text-dark p-1 me-2" onClick={() => { setEditingItem(i); setShowModal(true); }}>
                        <FaEdit />
                      </Button>
                      <Button variant="link" className="text-danger p-1">
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* Modal de Creación/Edición */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white" }}>
          <Modal.Title className="fw-bold fs-5">
            <FaBoxes className="me-2" /> {editingItem.id ? "Editar Item" : "Nuevo Item en Catálogo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">Código Interno / SKU</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingItem.codigo} 
                  onChange={(e) => setEditingItem({...editingItem, codigo: e.target.value})}
                  placeholder="Ej: SERV-001"
                />
              </Col>
              <Col md={8}>
                <Form.Label className="small fw-bold text-muted">Descripción del Producto/Servicio</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingItem.descripcion} 
                  onChange={(e) => setEditingItem({...editingItem, descripcion: e.target.value})}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">Categoría</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingItem.categoria} 
                  onChange={(e) => setEditingItem({...editingItem, categoria: e.target.value})}
                  placeholder="Alquiler, Suministros, etc."
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">Unidad de Medida</Form.Label>
                <Form.Select 
                  className="border-0 shadow-sm"
                  value={editingItem.unidad}
                  onChange={(e) => setEditingItem({...editingItem, unidad: e.target.value})}
                >
                  <option value="UND">Unidad (UND)</option>
                  <option value="GLB">Global (GLB)</option>
                  <option value="SER">Servicio (SER)</option>
                  <option value="MTR">Metros (MTR)</option>
                  <option value="KGR">Kilos (KGR)</option>
                </Form.Select>

              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">Precio Referencial (Sin IGV)</Form.Label>
                <Form.Control 
                  type="number"
                  className="border-0 shadow-sm text-end fw-bold" 
                  value={editingItem.precio_ref} 
                  onChange={(e) => setEditingItem({...editingItem, precio_ref: Number(e.target.value)})}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light px-4 pb-4">
          <Button variant="link" className="text-muted text-decoration-none" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button style={BTN_GRADIENT} className="rounded-pill px-4" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Guardar Item"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CatalogoItems;
